import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Trans, useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import {
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack,
  Box,
  Grid,
  Typography,
  Link as MuiLink,
} from '@mui/material';

import {
    VisibilityOffOutlined,
    VisibilityOutlined
} from '@mui/icons-material';

import apiClient from '../../services/api';
import { setToken } from '../../services/user';

import store from '../../store/index';
import { toggleMessageOpen } from '../../store/actions/Message';

import LanguageSelect from '../partials/LanguageSelect';

import LogoIcon from '../icons/LogoIcon';
import { ThemeToggle } from '../partials/CustomToggle';

import StyledPaper from '../styled/Paper';
import StyledTextField from '../styled/TextField';
import StyledButton from '../styled/Button';

import { setStorageValue } from '../../services/indexdb-service';

const login = async (email, password) => {
    return apiClient.post('api/login', { email, password }).then((response) => {
        if(response.data.success) {
            setToken(response.data.access_token);
            store.dispatch({ type: 'SET_LOGIN', payload: response.data.user });
            apiClient.defaults.headers.common['Authorization'] = `${response.data.access_token}`;

            setModules(response.data.modules);
        }
    })
};

const setModules = (modules) => {
    if(!modules || modules.length === 0) {
        return;
    }
    
    setStorageValue('modules', modules);
}

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});

    const [autofilled, setAutofilled] = useState(false);

    const { t } = useTranslation();

    const handleSubmit = (e) => {
        e.preventDefault();

        login(email, password).then(() => {
            store.dispatch(toggleMessageOpen(t('Successfully logged in!'), 'success'));
        }).catch(error => {
            store.dispatch(toggleMessageOpen(t(error.response.data.message), 'error'));
            setErrors(error.response.data);
        });
    };

    useEffect(() => {
        const check = setInterval(() => {
            const autofilled = !!document.getElementById('email_autofill')?.matches('*:-webkit-autofill');
            setAutofilled(autofilled);
        }, 100);
        return () => clearInterval(check);
    }, []);

    const buttonEnabled = (email && password) || autofilled;

    return (
        <>
            <Helmet>
                <title>{t('login')} - EasyBoard</title>
            </Helmet>
            <StyledPaper
                elevation={2}
                sx={{
                    padding: 8,
                    width: 500,
                    boxSizing: 'border-box',
                    maxWidth: '100%'
                }}
            >
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2}>
                        <Box title="EasyBoard" display="flex" justifyContent="center">
                            <LogoIcon />
                        </Box>
                        <Typography variant="h2" align="center">
                            <Trans>Log In to your account</Trans>
                        </Typography>
                        <StyledTextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            name="email"
                            type="email"
                            value={email}
                            required
                            placeholder={t('Your email')}
                            onChange={(e) => setEmail(e.target.value)}
                            InputProps={{
                                inputProps: {
                                    pattern: '.+@.+\\..+',
                                    title: 'login@example.any',
                                    id: 'email_autofill',
                                }
                            }}
                        />
                        <StyledTextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            name="password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            required
                            placeholder={t('Password')}
                            onChange={(e) => setPassword(e.target.value)}
                            InputProps={{
                                sx: { paddingRight: 0 },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(v=>!v)}
                                        >
                                            {showPassword ? (
                                                <VisibilityOffOutlined />
                                            ) : (
                                                <VisibilityOutlined />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        {errors.data && (
                            <FormHelperText error>
                                {errors.data}
                            </FormHelperText>
                        )}
                        <MuiLink
                            align="center"
                            sx={{ textDecoration: 'none', lineHeight: 2 }}
                            component={Link}
                            to="/recover"
                        >
                            <Trans>Did you forget your password?</Trans>
                        </MuiLink>
                        <StyledButton
                            type="submit"
                            color="primary"
                            variant="contained"
                            disabled={!buttonEnabled}
                            fullWidth
                        >
                            <Trans>Continue</Trans>
                        </StyledButton>
                    </Stack>
                    <Grid display="flex" justifyContent="space-between" sx={{ marginTop: 2, marginBottom: -2 }}>
                        <LanguageSelect />
                        <ThemeToggle />
                    </Grid>
                </form>
            </StyledPaper>
        </>
    );
};

export default Login;
